import Phaser from 'phaser';
import { APP_WINDOW, COLORS } from '../config/gameConfig';

/** Minimal interface every host App provides. Avoids a circular import on App. */
export interface ScrollableHost {
  readonly x: number;
  readonly y: number;
  readonly visible: boolean;
  readonly bodyWidth: number;
  readonly bodyHeight: number;
  readonly isMinimized: boolean;
  readonly titleBarHeight: number;
  containsPoint(x: number, y: number): boolean;
}

export interface ScrollableOptions {
  /** Local x of the viewport's top-left, relative to the body container. Default 0. */
  viewportXOffset?: number;
  /** Local y of the viewport's top-left, relative to the body container. Default 0. */
  viewportYOffset?: number;
  /** Width of the visible area. Default host.bodyWidth. */
  viewportWidth?: number;
  /** Height of the visible area. Default host.bodyHeight. */
  viewportHeight?: number;
}

/** Wraps a Phaser container in a clipped, wheel-driven scrollable viewport.
 *  - Geometry mask clips children outside the viewport rect.
 *  - Mouse wheel translates the target's y; clamped to content bounds.
 *  - A thin scrollbar appears on the right edge whenever content overflows.
 *  - The mask follows the host window's world position each frame.
 *  - Wheel events outside the viewport are ignored, so a window's title bar
 *    or tab strip can stay drag-friendly while only the body scrolls. */
export class Scrollable {
  private scrollY = 0;
  private contentHeight = 0;
  private readonly maskGraphics: Phaser.GameObjects.Graphics;
  private readonly scrollbarTrack: Phaser.GameObjects.Rectangle;
  private readonly scrollbarThumb: Phaser.GameObjects.Rectangle;
  private viewportX: number;
  private viewportY: number;
  private viewportW: number;
  private viewportH: number;

  private readonly onWheel = (
    pointer: Phaser.Input.Pointer,
    _objs: Phaser.GameObjects.GameObject[],
    _dx: number,
    deltaY: number,
  ): void => {
    if (this.host.isMinimized || !this.host.visible) return;
    if (!this.host.containsPoint(pointer.x, pointer.y)) return;
    if (!this.viewportContainsPointer(pointer.x, pointer.y)) return;
    this.applyDelta(-deltaY * 0.5);
  };

  /** True if the pointer is inside the scrollable's visible viewport (in
   *  world coordinates). Children that scrolled outside the viewport
   *  remain hit-test-able by Phaser — call this before acting on their
   *  input events to avoid clicks firing under the title bar / tab bar. */
  viewportContainsPointer(x: number, y: number): boolean {
    const vTop =
      this.host.y + this.host.titleBarHeight + APP_WINDOW.borderWidth + this.viewportY;
    const vLeft = this.host.x + APP_WINDOW.borderWidth + this.viewportX;
    if (y < vTop || y > vTop + this.viewportH) return false;
    if (x < vLeft || x > vLeft + this.viewportW) return false;
    return true;
  }

  private readonly onPostUpdate = (): void => this.sync();

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly host: ScrollableHost,
    private readonly target: Phaser.GameObjects.Container,
    bodyParent: Phaser.GameObjects.Container,
    options?: ScrollableOptions,
  ) {
    this.viewportX = options?.viewportXOffset ?? 0;
    this.viewportY = options?.viewportYOffset ?? 0;
    this.viewportW = options?.viewportWidth ?? host.bodyWidth;
    this.viewportH = options?.viewportHeight ?? host.bodyHeight;

    this.maskGraphics = scene.add.graphics();
    this.maskGraphics.fillStyle(0xffffff);
    this.maskGraphics.fillRect(0, 0, this.viewportW, this.viewportH);
    this.maskGraphics.setVisible(false);
    this.target.setMask(this.maskGraphics.createGeometryMask());

    const trackX = this.viewportX + this.viewportW - 4;
    this.scrollbarTrack = scene.add
      .rectangle(trackX, this.viewportY, 3, this.viewportH, COLORS.windowBorder, 0.4)
      .setOrigin(0, 0)
      .setVisible(false);
    bodyParent.add(this.scrollbarTrack);

    this.scrollbarThumb = scene.add
      .rectangle(trackX, this.viewportY, 3, 40, COLORS.markedTermUnderline, 0.85)
      .setOrigin(0, 0)
      .setVisible(false);
    bodyParent.add(this.scrollbarThumb);

    scene.input.on(Phaser.Input.Events.POINTER_WHEEL, this.onWheel);
    scene.events.on(Phaser.Scenes.Events.POST_UPDATE, this.onPostUpdate);
  }

  setContentHeight(h: number): void {
    this.contentHeight = h;
    this.applyDelta(0);
  }

  applyDelta(delta: number): void {
    const min = Math.min(0, this.viewportH - this.contentHeight);
    this.scrollY = Phaser.Math.Clamp(this.scrollY + delta, min, 0);
    this.target.y = this.viewportY + this.scrollY;
  }

  resize(viewportW: number, viewportH: number, viewportX = this.viewportX, viewportY = this.viewportY): void {
    this.viewportX = viewportX;
    this.viewportY = viewportY;
    this.viewportW = viewportW;
    this.viewportH = viewportH;
    this.maskGraphics.clear();
    this.maskGraphics.fillStyle(0xffffff);
    this.maskGraphics.fillRect(0, 0, viewportW, viewportH);
    const trackX = viewportX + viewportW - 4;
    this.scrollbarTrack.x = trackX;
    this.scrollbarTrack.y = viewportY;
    this.scrollbarTrack.setSize(3, viewportH);
    this.scrollbarThumb.x = trackX;
    this.applyDelta(0);
  }

  destroy(): void {
    this.scene.input.off(Phaser.Input.Events.POINTER_WHEEL, this.onWheel);
    this.scene.events.off(Phaser.Scenes.Events.POST_UPDATE, this.onPostUpdate);
  }

  private sync(): void {
    this.maskGraphics.x = this.host.x + APP_WINDOW.borderWidth + this.viewportX;
    this.maskGraphics.y =
      this.host.y + this.host.titleBarHeight + APP_WINDOW.borderWidth + this.viewportY;

    const overflow = this.contentHeight - this.viewportH;
    const hasOverflow = overflow > 0;
    const show = hasOverflow && !this.host.isMinimized;
    this.scrollbarTrack.setVisible(show);
    this.scrollbarThumb.setVisible(show);
    if (hasOverflow) {
      const thumbHeight = Math.max(24, (this.viewportH / this.contentHeight) * this.viewportH);
      const scrollFraction = -this.scrollY / overflow;
      const thumbY = this.viewportY + scrollFraction * (this.viewportH - thumbHeight);
      this.scrollbarThumb.setSize(3, thumbHeight);
      this.scrollbarThumb.y = thumbY;
    }
  }
}
